import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { AppSidebar } from '@/components/layout/Sidebar';
import { PageTitleProvider, usePageTitleValue } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import { SidebarInset, SidebarProvider, useSidebar } from '@/components/ui/sidebar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useBadgeCountRefresh } from '@/features/invoices/badges/useBadgeRefresh';
import type { Me } from '@/features/auth/api';

const SIDEBAR_COOKIE = 'sidebar_state';

/** Seed the collapsed/expanded state from the cookie the sidebar persists, so a reload keeps it. */
function readSidebarOpen(): boolean {
  if (typeof document === 'undefined') return true;
  const match = document.cookie.match(new RegExp(`(?:^|; )${SIDEBAR_COOKIE}=([^;]*)`));
  return match ? match[1] === 'true' : true;
}

/**
 * Mobile-only top bar: the persistent desktop rail keeps the brand and account control, so this
 * only exists below the breakpoint, where the sidebar is an off-canvas drawer that needs a trigger.
 * Shows the active page's heading (registered via `usePageTitle`) so the bar isn't just a redundant
 * brand label; pages hide their own heading on mobile to avoid showing it twice.
 */
function MobileHeader() {
  const { t } = useTranslation();
  const { isMobile, toggleSidebar } = useSidebar();
  const title = usePageTitleValue();
  if (!isMobile) return null;

  return (
    <header className="flex h-14 shrink-0 items-center gap-2 border-b bg-card px-4">
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('nav.open_menu')}
        onClick={() => toggleSidebar()}
      >
        <Menu className="size-5" />
      </Button>
      <span className="min-w-0 truncate font-semibold tracking-tight">{title ?? 'kstefan'}</span>
    </header>
  );
}

/**
 * Renders nothing; exists to run the badge-count poll at shell level so it drives the auto-refresh
 * heartbeat on every page (see `useBadgeCountRefresh`).
 *
 * Mounting it here also fixes a pre-existing gap: the nav badges were the only `useBadgeCounts` consumers,
 * and on mobile they live inside the sidebar `Sheet`, whose content Radix unmounts while closed — so the
 * counts did not poll at all on mobile with the drawer shut.
 */
function BadgeCountWatcher() {
  useBadgeCountRefresh();
  return null;
}

export function AppShell({ me }: { me: Me }) {
  return (
    <TooltipProvider>
      <PageTitleProvider>
        <SidebarProvider defaultOpen={readSidebarOpen()} className="h-dvh">
          {/* Gated on the issuing flag — `/api/v1/badge-counts` 404s when issuing is off. */}
          {me.issuingEnabled && <BadgeCountWatcher />}
          <AppSidebar me={me} />
          <SidebarInset className="min-h-0 min-w-0 overflow-hidden">
            <MobileHeader />
            <div className="min-h-0 flex-1 overflow-auto p-4 max-md:no-scrollbar md:p-6">
              <Outlet />
            </div>
          </SidebarInset>
        </SidebarProvider>
      </PageTitleProvider>
    </TooltipProvider>
  );
}
